import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IndustryService } from './industry.service';

export interface Festival {
  id: string;
  /** Inclusive local dates, YYYY-MM-DD */
  start: string;
  end: string;
  greeting: string;
  messages: string[];
}

// Add next year's dates here; the skin switches itself on and off by date
export const FESTIVALS: Festival[] = [
  {
    id: 'diwali',
    start: '2026-11-01',
    end: '2026-11-15',
    greeting: 'Happy Diwali',
    messages: [
      '🪔 Happy Diwali from Elixir POS! ✨ Wishing you a bright and prosperous festive season 🎆',
      '🪔 இனிய தீபாவளி நல்வாழ்த்துகள்! ✨ உங்கள் வணிகம் ஒளிமயமாக வளரட்டும் 🎆',
    ],
  },
];

// QA override: set to a festival id to force it on, or 'off' to hide it
const FORCE_KEY = 'elixir.festive.force';
const CLASS_PREFIX = 'festive-';
// Set on sign-in, read on the next load (the POS reloads after login)
const SIGNED_IN_KEY = 'elixir.festive.justSignedIn';

/**
 * Seasonal skin. Puts `festive-<id>` on <html> while a festival is running so the
 * styles in styles/components/_festive.scss apply. Restaurant and pharmacy stay plain.
 */
@Injectable({
  providedIn: 'root',
})
export class FestiveService {
  private festivalSubject = new BehaviorSubject<Festival | null>(null);
  public festival$: Observable<Festival | null> = this.festivalSubject.asObservable();

  constructor(private industryService: IndustryService) {
    this.industryService.industry$.subscribe(() => this.refresh());
  }

  get festival(): Festival | null {
    return this.festivalSubject.value;
  }

  private refresh(): void {
    const excluded = this.industryService.isRestaurant() || this.industryService.isPharmacy();
    const festival = excluded ? null : this.resolveFestival();
    this.applyClass(festival);
    this.festivalSubject.next(festival);
  }

  /** Call on a successful sign-in; the next app load plays the celebration once. */
  markSignedIn(): void {
    try {
      sessionStorage.setItem(SIGNED_IN_KEY, '1');
    } catch {
      // Storage blocked: no celebration, nothing else changes
    }
  }

  /** True once per festival, day and user, and only right after a sign-in. */
  takeCelebration(userKey: string): boolean {
    const festival = this.festival;
    try {
      if (sessionStorage.getItem(SIGNED_IN_KEY) !== '1') return false;
      sessionStorage.removeItem(SIGNED_IN_KEY);
      if (!festival || this.prefersReducedMotion()) return false;

      const key = `elixir.festive.celebrated.${festival.id}.${this.localDate()}.${userKey}`;
      if (localStorage.getItem(key) === '1') return false;
      localStorage.setItem(key, '1');
      return true;
    } catch {
      return false;
    }
  }

  prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  private resolveFestival(): Festival | null {
    const forced = this.readForce();
    if (forced === 'off') return null;
    if (forced) return FESTIVALS.find((f) => f.id === forced) ?? null;

    const today = this.localDate();
    return FESTIVALS.find((f) => today >= f.start && today <= f.end) ?? null;
  }

  private applyClass(festival: Festival | null): void {
    const root = document.documentElement;
    Array.from(root.classList).forEach((cls) => {
      if (cls.startsWith(CLASS_PREFIX)) root.classList.remove(cls);
    });
    if (festival) root.classList.add(CLASS_PREFIX + festival.id);
  }

  private localDate(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private readForce(): string | null {
    try {
      return localStorage.getItem(FORCE_KEY);
    } catch {
      return null;
    }
  }
}
